// ── Exports ("/exports") — Wave 10 (scoped CSV export, export≤view) ─────────
// CONTRACT TRUTH (apps/vjal-coaching-api/src): GET /exports/:export_type returns
// text/csv for NINE types, each with a per-type VIEW permission; event-history is
// staff-only; reports CSV is D-3 visibility-filtered; transcripts metadata-only.
// The backend does ONLY CSV — there is NO Excel/Word/PDF/PPT export.
//
// ── RELEASE GATE: EXPORT ≤ VIEW (FR-PERM-002), scope frozen at request ───────
// A role sees + can download ONLY `exportTypesForRole(role)` (never beyond its
// view scope). The chosen type is re-checked against the allow-list at download
// time (scope frozen at request); an out-of-scope selection is refused with NO
// request. PARTICIPANT is DENIED the module entirely (route + nav + page fail-
// closed) — its list of exportable types is empty.

import {
  Alert,
  Badge,
  Button,
  Module,
  ModuleBody,
  ModuleHead,
  PageFrame,
  ProTable,
  Select,
  useCollection,
  useToast,
  type CollectionColumnDef,
} from "@vjal/ui";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";

import { HonestState } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import type { ProductScreenContext } from "../product-route-contracts";
import { canAccessExportsModule } from "../../domain/logs-and-exports";
import {
  NON_CSV_EXPORT_FORMATS,
  NON_CSV_FORMAT_LABEL,
  exportTypeLabel,
  exportTypeNote,
  exportTypesForRole,
  runCsvExport,
  type CoachingExportType
} from "./exports-helpers";

const services = getCoachingServices();

type ExportTypeRow = {
  id: CoachingExportType;
  type: CoachingExportType;
  label: string;
  note: string;
};

export function ExportsPage({ role }: ProductScreenContext) {
  const moduleDenied = !canAccessExportsModule(role);
  const toast = useToast();

  // The EXPORT ≤ VIEW allow-list — the ONLY types this role may see/download.
  const types = useMemo<CoachingExportType[]>(() => exportTypesForRole(role), [role]);
  const [selected, setSelected] = useState<CoachingExportType | "">(types[0] ?? "");
  const [busy, setBusy] = useState(false);
  // EXPORT_FAILED posture marker for the last attempt (cleared on a new attempt).
  const [failed, setFailed] = useState<{ reference?: string } | null>(null);

  // ── Fail closed: a denied role (participant) gets permission-denied. ────────
  if (moduleDenied || types.length === 0) {
    return (
      <section aria-label="Exports" className="col-span-12" data-testid="exports-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to exports"
            message="Bulk CSV export is for coaching staff and assigned leads. Your own downloads stay on the report and resource pages."
          />
        </div>
      </section>
    );
  }

  const activeType = (selected || types[0]) as CoachingExportType;

  const onExport = async () => {
    setBusy(true);
    setFailed(null);
    const result = await runCsvExport(services.reports, role, activeType);
    setBusy(false);
    if (result.ok) {
      // useToast ONLY post-confirm: the download was triggered.
      toast.success(`Exporting ${exportTypeLabel(activeType)} as CSV`);
      return;
    }
    if (result.outOfScope) {
      // export>view refused before any request — should be unreachable from the
      // scoped chooser, but the gate bites if a forged value ever slips through.
      toast.error("That export is outside your access.");
      return;
    }
    setFailed({ reference: result.reference });
  };

  return (
    <ExportsReadyBody
      types={types}
      activeType={activeType}
      setSelected={setSelected}
      setFailed={setFailed}
      failed={failed}
      busy={busy}
      onExport={onExport}
    />
  );
}

function ExportsReadyBody({
  types,
  activeType,
  setSelected,
  setFailed,
  failed,
  busy,
  onExport,
}: {
  types: CoachingExportType[];
  activeType: CoachingExportType;
  setSelected: (value: CoachingExportType | "") => void;
  setFailed: (value: { reference?: string } | null) => void;
  failed: { reference?: string } | null;
  busy: boolean;
  onExport: () => void;
}) {
  const exportRows = useMemo<ExportTypeRow[]>(
    () =>
      types.map((type) => ({
        id: type,
        type,
        label: exportTypeLabel(type),
        note: exportTypeNote(type),
      })),
    [types],
  );
  const exportColumns: CollectionColumnDef<ExportTypeRow>[] = useMemo(
    () => [
      {
        id: "label",
        header: "Export",
        sortable: true,
        sortAccessor: (row) => row.label,
        searchValue: (row) => `${row.label} ${row.note}`,
        cell: (row) => row.label,
      },
      {
        id: "note",
        header: "Scope note",
        cell: (row) => <span className="text-sm text-ink-muted">{row.note}</span>,
      },
      {
        id: "format",
        header: "Format",
        cell: () => <Badge variant="info">CSV</Badge>,
      },
    ],
    [],
  );
  const collection = useCollection({
    rows: exportRows,
    columns: exportColumns,
    pageSize: 25,
    urlSync: false,
    getRowKey: (row) => row.id,
    views: "coaching-exports-types",
  });

  return (
    <PageFrame title="Exports" subtitle="Download CSV of records you can view — export never exceeds view scope." className="col-span-12">
      <section aria-label="Exports" className="flex flex-col gap-4" data-testid="exports-page">
        {/* ── The scoped CSV export action (export ≤ view) ──────────────────── */}
        <Module span={12}>
          <ModuleHead
            title="Export data (CSV)"
            subtitle="Download a CSV of the records you can view. The list below is scoped to your role."
          />
          <ModuleBody className="space-y-4" data-testid="exports-csv">
            <div className="grid gap-2 sm:max-w-sm" data-testid="exports-type-select">
              <label className="text-sm font-semibold text-strong" htmlFor="export-type">
                What to export
              </label>
              <Select
                id="export-type"
                aria-label="Export type"
                value={activeType}
                onChange={(event) => {
                  setSelected(event.target.value as CoachingExportType);
                  setFailed(null);
                }}
              >
                {types.map((type) => (
                  <option key={type} value={type}>
                    {exportTypeLabel(type)}
                  </option>
                ))}
              </Select>
              <p className="m-0 text-sm text-ink-muted">{exportTypeNote(activeType)}</p>
            </div>

            {failed ? (
              <div data-honest-posture="error">
                <Alert variant="error">
                  <span className="font-semibold">Export failed</span> — the CSV couldn't be generated. Nothing was
                  downloaded; try again in a moment.
                  {failed.reference ? ` (reference: ${failed.reference})` : ""}
                </Alert>
              </div>
            ) : null}

            <Button
              type="button"
              variant="primary"
              leftIcon={<Download size={16} />}
              loading={busy}
              onClick={onExport}
              aria-label={`Export ${exportTypeLabel(activeType)} CSV`}
              data-testid="exports-download"
            >
              Export CSV
            </Button>
          </ModuleBody>
        </Module>

        <div className="rounded-xl border border-line bg-white">
          {/* F3: real @vjal/ui ProTable-1 recipe export for exports IR. */}
          <ProTable
            collection={collection}
            className="p-0"
            tableProps={{
              caption: "CSV export types available to your role",
              emptyTitle: "No export types available",
              emptyMessage: "Your role has no exportable data families.",
            }}
            stableChipNames
          />
        </div>

        {/* ── Non-CSV formats — honest "not available" (CSV only for now) ────── */}
        <Module span={12}>
          <ModuleHead
            title="Other formats"
            subtitle="Excel, Word, PDF, and PowerPoint exports aren't available yet — CSV is the only format the export service produces today."
          />
          <ModuleBody className="space-y-3" data-testid="exports-non-csv">
            <div data-honest-posture="not-configured">
              <HonestState
                posture="not-configured"
                title="CSV only for now"
                message="The export service currently produces CSV only. Excel, Word, PDF, and PowerPoint exports will activate when the backend supports them — no other format is generated until then."
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {NON_CSV_EXPORT_FORMATS.map((format) => (
                <Badge key={format} variant="default">
                  {NON_CSV_FORMAT_LABEL[format]} — not available
                </Badge>
              ))}
            </div>
          </ModuleBody>
        </Module>
      </section>
    </PageFrame>
  );
}
